import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Collapse,
  Progress,
  Row,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { SimSessionWithReport, SessionReport, Recommendation } from "../../../../Entities/SimSession";
import interviewService from "../../../../Service/interview.service";

const { Text, Paragraph, Title } = Typography;

interface SimulationReportPanelProps {
  interviewSessionId: string;
}

const RECOMMENDATION_CONFIG: Record<
  Recommendation,
  { label: string; color: string; icon: React.ReactNode }
> = {
  strong_yes: {
    label: "Strong Yes",
    color: "success",
    icon: <CheckCircleOutlined />,
  },
  yes: {
    label: "Yes",
    color: "success",
    icon: <CheckCircleOutlined />,
  },
  borderline: {
    label: "Borderline",
    color: "warning",
    icon: <ExclamationCircleOutlined />,
  },
  no: {
    label: "No",
    color: "error",
    icon: <CloseCircleOutlined />,
  },
};

function scoreToPercent(score: number) {
  return Math.round(score * 100);
}

function scoreColor(score: number): string {
  if (score >= 0.8) return "#52c41a";
  if (score >= 0.6) return "#faad14";
  return "#ff4d4f";
}

const SimulationReportPanel: React.FC<SimulationReportPanelProps> = ({
  interviewSessionId,
}) => {
  const [loading, setLoading] = useState(true);
  const [simSessions, setSimSessions] = useState<SimSessionWithReport[]>([]);

  useEffect(() => {
    if (!interviewSessionId) return;
    setLoading(true);
    interviewService
      .getSimSessionsByInterviewSessionId(interviewSessionId)
      .then((data) => setSimSessions(data || []))
      .finally(() => setLoading(false));
  }, [interviewSessionId]);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spin tip="Loading simulation report..." />
      </div>
    );
  }

  if (simSessions.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="secondary">No simulation sessions found for this round.</Text>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 0" }}>
      {simSessions.map((sim, idx) => (
        <SimSessionCard key={sim._id} sim={sim} attemptNumber={idx + 1} />
      ))}
    </div>
  );
};

const SimSessionCard: React.FC<{
  sim: SimSessionWithReport;
  attemptNumber: number;
}> = ({ sim, attemptNumber }) => {
  const report = sim.report;

  return (
    <Card
      size="small"
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text strong>Attempt {attemptNumber}</Text>
          <SimStatusTag status={sim.status} terminationReason={sim.terminationReason} />
        </div>
      }
      extra={
        sim.endedAt && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {new Date(sim.endedAt).toLocaleDateString()}
          </Text>
        )
      }
    >
      <Row gutter={[8, 4]} style={{ marginBottom: 8 }}>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>Turns</Text>
          <br />
          <Text strong>{sim.currentTurnIndex}</Text>
        </Col>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>Phase Reached</Text>
          <br />
          <Text strong>{sim.simulationPhase}</Text>
        </Col>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>Mutation</Text>
          <br />
          <Text strong>{sim.mutationCount > 0 ? "Yes" : "No"}</Text>
        </Col>
      </Row>

      {!report && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {sim.status === "terminated"
            ? "Report is being generated..."
            : "Report not yet available."}
        </Text>
      )}

      {report && <ReportSection report={report} />}
    </Card>
  );
};

const ReportSection: React.FC<{ report: SessionReport }> = ({ report }) => {
  const recConfig = RECOMMENDATION_CONFIG[report.recommendation] ?? {
    label: report.recommendation,
    color: "default",
    icon: <QuestionCircleOutlined />,
  };

  const narrativeItems = report.narrative
    ? [
        { key: "scorecard", label: "Capability Scorecard", content: report.narrative.capabilityScorecard },
        { key: "trajectory", label: "State Trajectory", content: report.narrative.stateTrajectory },
        { key: "behavioral", label: "Behavioral Analysis", content: report.narrative.behavioralAnalysis },
        { key: "strengths", label: "Domain Strengths", content: report.narrative.domainStrengths },
        { key: "risks", label: "Risk Flags", content: report.narrative.riskFlags },
        { key: "recommendation", label: "Hiring Recommendation", content: report.narrative.hiringRecommendation },
        { key: "justification", label: "Justification", content: report.narrative.justificationNarrative },
      ]
    : [];

  return (
    <div>
      {/* Recommendation + Scores */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <Tag color={recConfig.color} icon={recConfig.icon} style={{ fontSize: 13, padding: "4px 10px" }}>
          {recConfig.label}
        </Tag>
        {report.overtimePenaltyApplied && (
          <Tag color="orange">Overtime Penalty Applied</Tag>
        )}
        {report.mutationOccurred && report.mutationRecoveryBonus && (
          <Tag color="blue">+{Math.round(report.mutationRecoveryBonus * 100)}% Recovery Bonus</Tag>
        )}
      </div>

      {/* Score bars */}
      <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>Behavior Score</Text>
          <Progress
            percent={scoreToPercent(report.behaviorScore)}
            strokeColor={scoreColor(report.behaviorScore)}
            size="small"
          />
        </Col>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>Domain Score</Text>
          <Progress
            percent={scoreToPercent(report.domainScore)}
            strokeColor={scoreColor(report.domainScore)}
            size="small"
          />
        </Col>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>Confidence</Text>
          <Progress
            percent={scoreToPercent(report.confidence)}
            strokeColor={scoreColor(report.confidence)}
            size="small"
          />
        </Col>
      </Row>

      {/* Metric breakdown */}
      <Collapse
        size="small"
        style={{ marginBottom: 12 }}
        items={[
          {
            key: "metrics",
            label: <Text style={{ fontSize: 13 }}>Metric Breakdown</Text>,
            children: (
              <div>
                <MetricRow label="Iteration Depth" entry={report.metricBreakdown.iterationDepth} />
                <MetricRow label="Risk Awareness" entry={report.metricBreakdown.riskAwareness} />
                <MetricRow label="Structured Thinking" entry={report.metricBreakdown.structuredThinking} />
                <MetricRow label="Tool Validation" entry={report.metricBreakdown.toolValidation} />
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Domain Score Detail</Text>
                  <Row gutter={[8, 4]} style={{ marginTop: 4 }}>
                    <Col span={8}>
                      <Text style={{ fontSize: 12 }}>Structural Correctness</Text>
                      <Progress percent={scoreToPercent(report.metricBreakdown.domainScore.structuralCorrectness)} size="small" />
                    </Col>
                    <Col span={8}>
                      <Text style={{ fontSize: 12 }}>Reasoning Depth</Text>
                      <Progress percent={scoreToPercent(report.metricBreakdown.domainScore.reasoningDepth)} size="small" />
                    </Col>
                    <Col span={8}>
                      <Text style={{ fontSize: 12 }}>Coverage</Text>
                      <Progress percent={scoreToPercent(report.metricBreakdown.domainScore.coverageCompleteness)} size="small" />
                    </Col>
                  </Row>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Dimensions covered: {report.metricBreakdown.domainScore.requiredDimensionsCovered.join(", ") || "none"} / {report.metricBreakdown.domainScore.totalRequiredDimensions} required
                  </Text>
                </div>
              </div>
            ),
          },
        ]}
      />

      {/* Narrative */}
      {narrativeItems.length > 0 && (
        <Collapse
          size="small"
          items={[
            {
              key: "narrative",
              label: <Text style={{ fontSize: 13 }}>AI Narrative Report</Text>,
              children: (
                <Collapse
                  size="small"
                  accordion
                  items={narrativeItems.map((n) => ({
                    key: n.key,
                    label: n.label,
                    children: <Paragraph style={{ fontSize: 13, marginBottom: 0 }}>{n.content}</Paragraph>,
                  }))}
                />
              ),
            },
          ]}
        />
      )}

      {!report.narrative && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          AI narrative is being generated...
        </Text>
      )}
    </div>
  );
};

const MetricRow: React.FC<{
  label: string;
  entry: { rawScore: number; weightedScore: number; weight: number; antiGamingApplied: boolean; antiGamingDetails: string | null };
}> = ({ label, entry }) => (
  <Row gutter={[8, 0]} align="middle" style={{ marginBottom: 6 }}>
    <Col span={10}>
      <Text style={{ fontSize: 12 }}>{label}</Text>
      {entry.antiGamingApplied && (
        <Tag color="orange" style={{ marginLeft: 4, fontSize: 10 }}>Anti-gaming</Tag>
      )}
    </Col>
    <Col span={10}>
      <Progress
        percent={scoreToPercent(entry.rawScore)}
        strokeColor={scoreColor(entry.rawScore)}
        size="small"
      />
    </Col>
    <Col span={4}>
      <Text type="secondary" style={{ fontSize: 11 }}>w={entry.weight}</Text>
    </Col>
  </Row>
);

const SimStatusTag: React.FC<{
  status: string;
  terminationReason?: string;
}> = ({ status, terminationReason }) => {
  if (status === "terminated") {
    const color = terminationReason === "success" ? "success" : terminationReason === "failure" ? "error" : "default";
    const label = terminationReason
      ? terminationReason.charAt(0).toUpperCase() + terminationReason.slice(1)
      : "Terminated";
    return <Tag color={color}>{label}</Tag>;
  }
  const map: Record<string, { color: string; label: string }> = {
    pending: { color: "default", label: "Pending" },
    scenarioReady: { color: "blue", label: "Scenario Ready" },
    active: { color: "processing", label: "Active" },
    paused: { color: "warning", label: "Paused" },
    scenarioGenerationFailed: { color: "error", label: "Generation Failed" },
  };
  const cfg = map[status] ?? { color: "default", label: status };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
};

export default SimulationReportPanel;
